import { motion } from 'framer-motion';
import {
  Grid3x3, Receipt,
  Users2, FolderOpen,
  ChevronRight, ChevronDown, ChevronsLeft, Bell, Globe, AlertCircle
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Brand mark — real Shafafiya logo image                            */
/* ------------------------------------------------------------------ */
function ShafafiyaLogo() {
  return (
    <img
      src="/assets/shafafiya-logo.png"
      alt="Shafafiya — Health Insurance Exchange"
      className="h-10 w-auto object-contain"
      style={{ maxWidth: 170 }}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Nav item                                                          */
/* ------------------------------------------------------------------ */
function NavItem({ Icon, label, active, expanded, onToggle }) {
  return (
    <div
      onClick={onToggle}
      className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg cursor-pointer text-[13px] font-body transition-colors ${
        active
          ? 'bg-teal-600 text-white font-semibold'
          : 'text-[#4B5563] hover:bg-[#F3F4F6]'
      }`}
    >
      <Icon size={16} className={active ? 'text-white' : 'text-[#6B7280]'} />
      <span className="flex-1">{label}</span>
      {expanded
        ? <ChevronDown size={14} className={active ? 'text-white/90' : 'text-[#9CA3AF]'} />
        : <ChevronRight size={13} className="text-[#9CA3AF]" />}
    </div>
  );
}

function SubNavItem({ label, active }) {
  return (
    <div
      className={`ml-8 px-3 py-2 rounded-lg text-[13px] font-body cursor-pointer transition-colors ${
        active
          ? 'bg-teal-50 text-teal-700 font-semibold'
          : 'text-[#6B7280] hover:bg-[#F3F4F6]'
      }`}
    >
      {label}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sidebar — matches reference: Home / Mandatory tariffs /            */
/*  Transactions / Survey / Notification engine / Manage / Resources   */
/* ------------------------------------------------------------------ */
function Sidebar({ activeGroup = 'Manage', activeSub = 'Users', reportActive = false, onReportClick = null }) {
  const groups = [
    { Icon: FolderOpen,     label: 'Resources' },
    { Icon: Grid3x3,        label: 'Mandatory tariffs' },
    { Icon: Receipt,        label: 'Transactions' },
    { Icon: Users2,         label: 'Manage',             subs: ['Roles', 'Users', 'Facilities'] },
  ];

  return (
    <aside className="w-[250px] flex-shrink-0 flex flex-col bg-white border-r border-[#E5E7EB]">
      {/* Logo */}
      <div className="px-5 py-5">
        <ShafafiyaLogo />
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 pt-2 pb-4 space-y-1 overflow-y-auto">
        {groups.map(g => {
          const isActive = g.label === activeGroup;
          const isExpanded = isActive && g.subs;
          return (
            <div key={g.label}>
              <NavItem Icon={g.Icon} label={g.label} active={isActive} expanded={!!isExpanded} />
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-1 space-y-0.5"
                >
                  {g.subs.map(s => (
                    <SubNavItem key={s} label={s} active={s === activeSub} />
                  ))}
                </motion.div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-[#E5E7EB] px-3 py-3 space-y-1">
        <div className="flex items-center gap-2 px-3.5 py-2 rounded-lg border border-[#E5E7EB] text-[12px] font-body text-[#4B5563] hover:bg-[#F3F4F6] cursor-pointer">
          <ChevronsLeft size={14} /><span>Collapse menu</span>
        </div>
        {/* Report Issue — always present at bottom; pulses + highlights when active */}
        <motion.div
          data-testid="sidebar-report-issue"
          onClick={onReportClick || undefined}
          animate={reportActive
            ? {
                backgroundColor: ['#F0FDFA', '#CCFBF1', '#F0FDFA'],
                boxShadow: ['0 0 0 0 rgba(13,148,136,0)', '0 0 0 6px rgba(13,148,136,0.25)', '0 0 0 0 rgba(13,148,136,0)'],
                scale: [1, 1.04, 1],
              }
            : {}}
          transition={reportActive ? { duration: 1.8, repeat: Infinity } : {}}
          className={`flex items-center gap-2.5 px-3.5 py-2 rounded-lg text-[13px] font-body cursor-pointer transition-colors ${
            reportActive
              ? 'text-teal-700 font-semibold bg-teal-50 border border-teal-200'
              : 'text-[#4B5563] hover:bg-[#F3F4F6]'
          }`}
        >
          <AlertCircle size={15} className={reportActive ? 'text-teal-600' : 'text-[#6B7280]'} />
          <span>Report Issue</span>
        </motion.div>
        <div className="flex items-center gap-2.5 px-3.5 py-2.5">
          <div className="w-8 h-8 rounded-full bg-orange-400 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-[10px] font-bold">OS</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[12px] font-body font-semibold text-[#0A0A0A] truncate">Omar Saba</p>
            <p className="text-[10px] font-body text-[#6B7280] truncate">m@example.com</p>
          </div>
          <ChevronDown size={12} className="text-[#9CA3AF] flex-shrink-0" />
        </div>
      </div>
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/*  Top bar — bell with count + عربي language toggle                   */
/* ------------------------------------------------------------------ */
function TopBar() {
  return (
    <div className="flex items-center justify-end gap-3 px-6 py-3 bg-white border-b border-[#E5E7EB] flex-shrink-0">
      <button className="relative w-9 h-9 rounded-full bg-[#F3F4F6] flex items-center justify-center hover:bg-[#E5E7EB]">
        <Bell size={16} className="text-[#4B5563]" />
        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-teal-600 text-white text-[9px] font-bold flex items-center justify-center">10</span>
      </button>
      <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E7EB] text-[12px] font-body text-[#4B5563] hover:bg-[#F3F4F6]">
        <Globe size={14} className="text-[#6B7280]" />
        <span>عربي</span>
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  PortalShell                                                       */
/* ------------------------------------------------------------------ */
export default function PortalShell({ children, activeItem = 'User', overlay = null, fullOverlay = null, reportActive = false, onReportClick = null }) {
  // Map the scene's declared active item to the reference sidebar groups.
  // All User Management scenes land on "Manage > Users".
  const mapping = {
    User:   { group: 'Manage', sub: 'Users' },
    Users:  { group: 'Manage', sub: 'Users' },
    Survey: { group: 'Survey', sub: 'Survey hub' },
    Home:   { group: 'Home',   sub: null },
  };
  const { group, sub } = mapping[activeItem] || mapping.User;

  return (
    <div data-testid="portal-shell" className="flex items-start justify-center h-full w-full overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative bg-white rounded-xl border border-black/5 shadow-2xl overflow-hidden flex"
        style={{ width: '100%', maxWidth: 1400, height: 660 }}
      >
        <Sidebar activeGroup={group} activeSub={sub} reportActive={reportActive} onReportClick={onReportClick} />

        <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
          <TopBar />

          {/* Main content — relative so absolute children (cursor, etc.) anchor here */}
          <div className="flex-1 overflow-y-auto relative min-h-0">
            <div className="min-h-full h-full">
              {children}
            </div>
            {overlay}
          </div>
        </div>

        {/* Full-portal overlay — covers sidebar + content (coords from portal top-left) */}
        {fullOverlay}
      </motion.div>
    </div>
  );
}
